import { Box, Divider, Stack } from '@mui/material';
import { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { Dispatch, useState } from 'react';
import { Site, UserDetails } from '../../../constants/Common';
import { Timesheet } from '../../../constants/Timesheet/Timesheet';
import { FirebaseApi } from '../../../firebase/firebaseApi';
import { useFeatureFlagContext } from '../../../providers/featureFlags/Provider';
import { AddNew } from '../../DataTable/AddNew';
import { DateDataTableWithID } from '../../DataTable/DateDataTableWithID';
import { formatShortDate } from '../../helpers/dateFormatters';
import { dateWithinCurrentWeeksRange } from '../../helpers/dateUtilities';
import { LoadingDots } from '../../Management/subcomponents/LoadingDots';
import { MyTimeState } from '../StateManagement/actions';
import { AddTimesheetDialog } from './AddTimesheetDialog';
import { SingleTimesheetView } from './SingleTimesheetView';
import { SingleTimesheetViewDeprecated } from './SingleTimesheetViewDeprecated';

type CurrentTimesheetsFirebaseCalls =
	| 'getCompany'
	| 'getSite'
	| 'activitySubscription'
	| 'updateTimesheetActivities'
	| 'deleteActivities'
	| 'deleteTimesheet'
	| 'setActivities'
	| 'getDownloadUrl'
	| 'preApproveTimesheet'
	| 'createTimesheet'
	| 'getLimitedContractsByEmployeeSiteSupplierAccepterStatus'
	| 'getLimitedContractByEmployeeSupplierAccepterStatus'
	| 'getUser';

export type CurrentTimesheetsProps = {
	userDetails: UserDetails;
	selected: MyTimeState['timesheetID'];
	setSelected: (timesheetID: string) => void;
	endDate: Date;
	setEndDate: Dispatch<Date>;
	sites: Record<string, Site>;
	timesheets: Record<string, Timesheet>;
	timesheetsHistory: Record<string, Timesheet>;
	navigateToTimesheet: (
		targetTimesheet: Pick<
			Timesheet,
			'id' | 'weekEnding' | 'timesheetStatus'
		>,
	) => void;
	loading: boolean;
	firebaseApi: Pick<FirebaseApi, CurrentTimesheetsFirebaseCalls>;
};

export const CurrentTimesheets = ({
	userDetails,
	selected,
	setSelected,
	endDate,
	setEndDate,
	sites,
	timesheets,
	timesheetsHistory,
	navigateToTimesheet,
	loading,
	firebaseApi,
}: CurrentTimesheetsProps): JSX.Element => {
	const featureFlags = useFeatureFlagContext();
	const createTimesheetsV3 = featureFlags.get('enableCreateMyTimesheetsV3');

	const [open, setOpen] = useState(false);

	const cellWidth = '33%';
	const columns: MUIDataTableColumnDef[] = [
		{
			label: 'Site',
			name: 'site',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
				customBodyRender: (site: Timesheet['site']) => site.name,
			},
		},
		{
			label: 'Date Submitted',
			name: 'dateSubmitted',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
				customBodyRender: (dateSubmitted: Timesheet['dateSubmitted']) =>
					dateSubmitted
						? formatShortDate(dateSubmitted.toDate())
						: '-',
			},
		},
		{
			label: 'Status',
			name: 'timesheetStatus',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
	];

	const tableLoadingOpt: MUIDataTableOptions = {
		textLabels: {
			body: {
				noMatch: loading ? (
					<LoadingDots />
				) : (
					'Sorry, no matching timesheets found'
				),
			},
		},
		tableBodyHeight: 'calc(100vh - 298px)',
	};

	const handleClickOpen = (): void => {
		setOpen(true);
	};

	const handleDialogClose = (): void => {
		setOpen(false);
	};

	const tableTitle = (
		<AddNew
			onClick={(): void => setOpen(true)}
			type="current"
			title="Add Timesheet"
			disabled={!dateWithinCurrentWeeksRange(endDate)}
		/>
	);

	return (
		<Stack
			direction={{
				xs: 'column',
				sm: 'column',
				md: 'column',
				lg: 'row',
			}}
			divider={
				<Divider
					sx={{ ml: 1, mr: 1 }}
					orientation="vertical"
					flexItem
				/>
			}
			spacing={2}>
			<Box flex="1">
				<DateDataTableWithID
					title={tableTitle}
					tableData={Object.values(timesheets)}
					columns={columns}
					date={[endDate, setEndDate]}
					selection={[selected, setSelected]}
					customTableOptions={tableLoadingOpt}
					type="current"
					onClick={handleClickOpen}
					allowFuture
					weekEnding
					showWeekRange={false}
				/>
				<AddTimesheetDialog
					open={open}
					loading={loading}
					handleClose={handleDialogClose}
					goToTimesheet={(timesheet): void =>
						navigateToTimesheet(timesheet)
					}
					currentTimesheets={Object.values(timesheets)}
					timesheetsHistory={Object.values(timesheetsHistory)}
					sites={sites}
					userDetails={userDetails}
					setWeek={setEndDate}
					weekEnding={endDate}
					firebaseApi={firebaseApi}
				/>
			</Box>
			{createTimesheetsV3 ? (
				<SingleTimesheetView />
			) : (
				<SingleTimesheetViewDeprecated
					timesheet={timesheets[selected ?? '']}
					userDetails={userDetails}
					displayType="current"
					weekEnding={[endDate, setEndDate]}
					loading={loading}
					firebaseApi={firebaseApi}
				/>
			)}
		</Stack>
	);
};
